import { Fragment, useMemo, useState } from 'react';
import { DeleteButton, ErrorBanner, Empty, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { useResource } from '../lib/hooks';
import type {
  BusinessUnit,
  MeasurableComparison,
  MeasurableUnit,
  ScorecardCadence,
  ScorecardItem,
} from '../lib/types';

/**
 * Settings > Scorecard Items — the library a process is measured out of.
 *
 * Before this screen the process form picked from the EOS module's scorecard
 * templates, flattened. That meant the same measurable appeared once per
 * template that used it, and nobody could add one without editing somebody's
 * scorecard. The library is now a table of its own, seeded from the EOS app
 * in migration 098 and maintained here.
 *
 * Admin-only, like everything else in Settings — this is the vocabulary the
 * rest of the app measures itself in, not a working list.
 */
export function ScorecardItemsPage() {
  const items = useResource<ScorecardItem[]>('/scorecard-items');
  const units = useResource<BusinessUnit[]>('/business-units');
  const [editing, setEditing] = useState<ScorecardItem | 'new' | null>(null);
  const [term, setTerm] = useState('');
  const [unitId, setUnitId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const all = useMemo(() => items.data ?? [], [items.data]);

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return all.filter((i) => {
      if (unitId !== null && i.BusinessUnitId !== unitId) return false;
      if (!needle) return true;
      return (
        i.Name.toLowerCase().includes(needle) ||
        (i.Category ?? '').toLowerCase().includes(needle) ||
        (i.BusinessUnitName ?? '').toLowerCase().includes(needle) ||
        (i.Description ?? '').toLowerCase().includes(needle)
      );
    });
  }, [all, term, unitId]);

  /* Grouped in the order the API sent them, which puts the uncategorised pile
     last. Walked rather than re-sorted, so the two cannot disagree. */
  const groups = useMemo(() => {
    const out: { category: string | null; rows: ScorecardItem[] }[] = [];
    for (const i of matches) {
      const last = out[out.length - 1];
      if (last && last.category === i.Category) last.rows.push(i);
      else out.push({ category: i.Category, rows: [i] });
    }
    return out;
  }, [matches]);

  /* Only the units that hold something. Offering all twelve when four of them
     have no items is a filter that mostly returns nothing. */
  const unitFilters = useMemo(() => {
    const counts = new Map<number, { name: string; n: number }>();
    for (const i of all) {
      if (i.BusinessUnitId === null) continue;
      const seen = counts.get(i.BusinessUnitId);
      if (seen) seen.n += 1;
      else counts.set(i.BusinessUnitId, { name: i.BusinessUnitName ?? '—', n: 1 });
    }
    return [...counts.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [all]);

  if (items.loading) return <Loading label="Loading the scorecard library" />;
  if (items.error) return <ErrorBanner message={items.error} />;

  const attached = all.filter((i) => i.ProcessCount > 0).length;
  const inactive = all.filter((i) => !i.IsActive).length;
  const categories = new Set(all.filter((i) => i.Category).map((i) => i.Category)).size;

  return (
    <>
      <PageHead
        title="Scorecard Items"
        lede="The library every process is measured out of. Add an item here and it becomes available to attach to a process; deactivate one and it leaves the picker without changing any process that already names it."
        actions={
          <div className="row">
            <input
              className="input"
              style={{ width: 220 }}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search the library…"
              aria-label="Search the library"
            />
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              Add an item
            </button>
          </div>
        }
      />

      {error && <ErrorBanner message={error} />}

      <p className="small muted mb-2">
        <strong className="strong">{all.length}</strong> items ·{' '}
        <strong className="strong">{categories}</strong> categories ·{' '}
        <strong className="strong">{attached}</strong> attached to a process
        {inactive > 0 ? ` · ${inactive} inactive` : ''}
      </p>

      <div className="wfr-kinds mb-4">
        <button
          type="button"
          className={`wfr-kind${unitId === null ? ' on' : ''}`}
          onClick={() => setUnitId(null)}
        >
          Every unit
        </button>
        {unitFilters.map(([id, u]) => (
          <button
            key={id}
            type="button"
            className={`wfr-kind${unitId === id ? ' on' : ''}`}
            onClick={() => setUnitId(unitId === id ? null : id)}
          >
            {u.name} <span className="wfr-count">{u.n}</span>
          </button>
        ))}
      </div>

      {matches.length === 0 ? (
        <Empty title={all.length === 0 ? 'The library is empty' : 'Nothing matches'}>
          {all.length === 0
            ? 'Add the first item and it becomes available to every process.'
            : 'No item matches that search.'}
        </Empty>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Business unit</th>
                  <th>Target</th>
                  <th className="num">Processes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g.category ?? '\u0000uncategorised'}>
                    <tr>
                      <td colSpan={5} className="sci-group">
                        {g.category ?? 'Uncategorised'}
                        <span className="xs subtle ml-2">
                          {g.rows.length} {g.rows.length === 1 ? 'item' : 'items'}
                        </span>
                      </td>
                    </tr>
                    {g.rows.map((i) => (
                  <tr key={i.ScorecardItemId}>
                    <td>
                      <div className="strong">
                        {i.Name}
                        {!i.IsActive && <span className="badge badge-neutral ml-2">Inactive</span>}
                      </div>
                      {i.Description && <div className="xs muted">{i.Description}</div>}
                    </td>
                    <td className="small muted">{i.BusinessUnitName ?? '—'}</td>
                    <td className="tabular">{target(i)}</td>
                    <td className="num">{i.ProcessCount || '—'}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditing(i)}
                      >
                        Edit
                      </button>
                      {/* Only offered where it is actually allowed. An item a
                          process points at is deactivated, not deleted, and the
                          API refuses the delete either way — this stops the
                          button being an invitation to find that out. */}
                      {i.ProcessCount === 0 && (
                        <DeleteButton
                          what={i.Name}
                          onConfirm={() =>
                            void api
                              .del(`/scorecard-items/${i.ScorecardItemId}`)
                              .then(() => items.reload())
                              .catch((err: unknown) =>
                                setError(err instanceof Error ? err.message : 'Could not delete'),
                              )
                          }
                        />
                      )}
                    </td>
                  </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <ItemEditor
          item={editing === 'new' ? null : editing}
          units={units.data ?? []}
          categories={[
            ...new Set(all.map((i) => i.Category).filter((c): c is string => !!c)),
          ].sort()}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            items.reload();
          }}
        />
      )}
    </>
  );
}

function target(i: { Comparison: string; Goal: number | null; Unit: string }): string {
  if (i.Goal === null) return '—';
  return `${i.Comparison} ${i.Unit === '$' ? '$' : ''}${i.Goal}${i.Unit === '%' ? '%' : ''}`;
}

const UNITS: MeasurableUnit[] = ['#', '$', '%'];
const COMPARISONS: MeasurableComparison[] = ['>=', '<=', '='];
const CADENCES: ScorecardCadence[] = ['weekly', 'monthly', 'quarterly', 'annual'];

function ItemEditor({
  item,
  units,
  categories,
  onClose,
  onSaved,
}: {
  item: ScorecardItem | null;
  units: BusinessUnit[];
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [category, setCategory] = useState(item?.Category ?? '');
  const [businessUnitId, setBusinessUnitId] = useState<number | null>(item?.BusinessUnitId ?? null);
  const [name, setName] = useState(item?.Name ?? '');
  const [description, setDescription] = useState(item?.Description ?? '');
  const [unit, setUnit] = useState<MeasurableUnit>(item?.Unit ?? '#');
  /* Held as the input's string: '' is "no target", which is a real answer for
     an item somebody only wants to watch the trend of. */
  const [goal, setGoal] = useState(item?.Goal != null ? String(item.Goal) : '');
  const [comparison, setComparison] = useState<MeasurableComparison>(item?.Comparison ?? '>=');
  const [cadence, setCadence] = useState<string>(item?.Cadence ?? '');
  const [isActive, setIsActive] = useState(item?.IsActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const body = {
      category: category.trim() || null,
      businessUnitId,
      name: name.trim(),
      description: description.trim() || null,
      unit,
      goal: goal.trim() === '' ? null : Number(goal),
      comparison,
      cadence: cadence === '' ? null : cadence,
      isActive,
    };
    try {
      if (item) await api.put(`/scorecard-items/${item.ScorecardItemId}`, body);
      else await api.post('/scorecard-items', body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={item ? `Edit ${item.Name}` : 'Add a scorecard item'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Name">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <div className="grid grid-2">
        <Field label="Category" hint="How the EOS app grouped it. Empty is uncategorised.">
          <input
            className="input"
            list="sci-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          {/* A datalist rather than a select: the 17 categories came from
              somebody else's free text, and a new one has to be typeable. */}
          <datalist id="sci-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field label="Business unit" hint="Whose number this is.">
          <select
            className="input"
            value={businessUnitId ?? ''}
            onChange={(e) =>
              setBusinessUnitId(e.target.value === '' ? null : Number(e.target.value))
            }
          >
            <option value="">Not set</option>
            {units.map((u) => (
              <option key={u.BusinessUnitId} value={u.BusinessUnitId}>
                {u.Name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Description" hint="What a reader needs to know that the name does not.">
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <div className="grid grid-3">
        <Field label="Comparison">
          <select
            className="input"
            value={comparison}
            onChange={(e) => setComparison(e.target.value as MeasurableComparison)}
          >
            {COMPARISONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Target" hint="Leave empty to watch the trend only.">
          <input
            className="input"
            inputMode="decimal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
          />
        </Field>
        <Field label="Unit">
          <select
            className="input"
            value={unit}
            onChange={(e) => setUnit(e.target.value as MeasurableUnit)}
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Cadence" hint="How often the number is read.">
        <select className="input" value={cadence} onChange={(e) => setCadence(e.target.value)}>
          <option value="">Not set</option>
          {CADENCES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="In the picker"
        hint={
          item && item.ProcessCount > 0
            ? `Attached to ${item.ProcessCount} process(es). Turning this off leaves them exactly as they are — it only takes the item out of the list for new ones.`
            : 'An inactive item stays in the library but is not offered when attaching to a process.'
        }
      >
        <label className="checkbox">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span>Offer this item when attaching to a process</span>
        </label>
      </Field>
    </Modal>
  );
}
